import type { LocationQuery, LocationQueryRaw } from 'vue-router'

import type { QueryFilterSchema } from './types'

// `page`/`sortBy` are pagination and sorting, not filters — their defaults are always
// seeded, even when the URL already carries a filter (see `computeInitialFilter`).
const FILTER_NEUTRAL_KEYS = ['page', 'sortBy']

export const queryEqual = (a: LocationQueryRaw, b: LocationQuery): boolean => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])
  for (const key of keys) {
    const av = a[key]
    const bv = b[key]
    if (Array.isArray(av) || Array.isArray(bv)) {
      const aa = Array.isArray(av) ? av : av === undefined ? [] : [av]
      const bb = Array.isArray(bv) ? bv : bv === undefined ? [] : [bv]
      if (aa.length !== bb.length || aa.some((item, index) => item !== bb[index])) {
        return false
      }
      continue
    }
    if (av !== bv) {
      return false
    }
  }
  return true
}

export const parseAll = <T extends Record<string, unknown>>(
  schema: QueryFilterSchema<T>,
  query: LocationQuery,
  defaults?: Partial<T>,
): Partial<T> => {
  const result = {} as Record<string, unknown>
  for (const key of Object.keys(schema) as (keyof T)[]) {
    const field = schema[key]
    if (!field) {
      continue
    }
    const parsed = field.parse(query[key as string], query)
    result[key as string] = parsed === undefined && defaults ? defaults[key] : parsed
  }
  return result as Partial<T>
}

export const serializeAll = <T extends Record<string, unknown>>(
  schema: QueryFilterSchema<T>,
  filter: T,
  base: LocationQuery,
): LocationQueryRaw => {
  const out: LocationQueryRaw = { ...base }
  for (const key of Object.keys(schema) as (keyof T)[]) {
    schema[key]?.serialize(filter[key], out)
  }
  return out
}

export const shallowEqualValue = (a: unknown, b: unknown): boolean => {
  if (a === b) {
    return true
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, index) => item === b[index])
  }
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    const aObj = a as Record<string, unknown>
    const bObj = b as Record<string, unknown>
    const keys = new Set([...Object.keys(aObj), ...Object.keys(bObj)])
    return [...keys].every((key) => aObj[key] === bObj[key])
  }
  return false
}

export const changedKeys = <T extends Record<string, unknown>>(a: T, b: T): string[] => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])
  return [...keys].filter((key) => !shallowEqualValue(a[key], b[key]))
}

// One-level clone so the snapshot keeps independent array/object references. This
// matches `shallowEqualValue`'s depth, so a later in-place mutation of a nested value
// (e.g. `filter.value.statuses.push(...)`) is still detected as a change.
export const snapshot = <T extends Record<string, unknown>>(value: T): T => {
  const out = {} as Record<string, unknown>
  for (const [key, item] of Object.entries(value)) {
    out[key] = Array.isArray(item)
      ? [...item]
      : item && typeof item === 'object'
        ? { ...item }
        : item
  }
  return out as T
}

export const emptyFilter = <T extends Record<string, unknown>>(
  schema: QueryFilterSchema<T>,
): Partial<T> => {
  const out = {} as Record<string, unknown>
  for (const key of Object.keys(schema)) {
    out[key] = undefined
  }
  return out as Partial<T>
}

/**
 * Builds the initial filter from the URL. Defaults are seeded only when the URL has no
 * active filter: a shared/bookmarked link that already carries filters is taken as-is,
 * so a configured default (e.g. the user's advertiser group) can't silently narrow it.
 * `page`/`sortBy` defaults always apply since they aren't filters.
 */
export const computeInitialFilter = <T extends Record<string, unknown>>(
  schema: QueryFilterSchema<T>,
  query: LocationQuery,
  defaults: T,
): T => {
  // Compare against the values an empty URL parses to (a field's natural "empty" may be
  // `false`/`[]`, not `undefined`), so only a real filter value counts as active.
  const fromUrl = parseAll(schema, query)
  const fromEmpty = parseAll(schema, {})
  const hasActiveFilter = Object.keys(fromUrl).some(
    (key) => !FILTER_NEUTRAL_KEYS.includes(key) && !shallowEqualValue(fromUrl[key], fromEmpty[key]),
  )
  const seeded = hasActiveFilter
    ? FILTER_NEUTRAL_KEYS.reduce(
        (acc, key) => {
          if (key in defaults) {
            acc[key] = defaults[key]
          }
          return acc
        },
        {} as Record<string, unknown>,
      )
    : defaults
  return { ...seeded, ...parseAll(schema, query, seeded as Partial<T>) } as T
}
